/**
 * hook for creating and deleting document groups within a metadoc
 * manages grouping operations and updates selection state
 */
import { KaboodleToaster } from "components/common";
import {
  useCreateDocumentGroupMutation,
  useDeleteDocumentGroupMutation,
} from "services/document-group-api";

import { METADOC_GROUP_ID_PREFIX } from "../../MetadocConstants";
import { getDocumentTypeLabel } from "../../utils/documentTypeUtils";

import type { DocumentGroup } from "services/document-group-api";
import type { GroupingSource } from "services/metadoc-api";

export const useDocumentGrouping = (
  metadocId: number,
  documentGroups: DocumentGroup[],
  selectedPages: Set<number>,
  setSelectedPages: (pages: Set<number>) => void,
  groupingSource: GroupingSource,
  onSuccess?: () => void,
  onRegenerationJobCreated?: (jobId: number) => void,
  onRegenerationRequestStateChange?: (isRequesting: boolean) => void,
) => {
  const [createDocumentGroup] = useCreateDocumentGroupMutation();
  const [deleteDocumentGroup] = useDeleteDocumentGroupMutation();

  const validateContiguousPages = (pages: Set<number>): boolean => {
    if (pages.size === 0) return false;

    const sortedPages = Array.from(pages).sort((a, b) => a - b);
    for (let i = 1; i < sortedPages.length; i++) {
      if (sortedPages[i] !== sortedPages[i - 1] + 1) {
        return false;
      }
    }
    return true;
  };

  const findOverlappingGroups = (
    startPg: number,
    endPg: number,
  ): DocumentGroup[] => {
    return documentGroups
      .filter((group) => group.source === groupingSource)
      .filter((group) => startPg <= group.end_pg && endPg >= group.start_pg);
  };

  const assignDocumentType = async (documentType: string): Promise<boolean> => {
    if (selectedPages.size === 0) {
      KaboodleToaster.error("Please select at least one page");
      return false;
    }

    // validate contiguous pages
    if (!validateContiguousPages(selectedPages)) {
      KaboodleToaster.error("Please select contiguous pages only");
      return false;
    }

    const sortedPages = Array.from(selectedPages).sort((a, b) => a - b);
    const startPg = sortedPages[0];
    const endPg = sortedPages[sortedPages.length - 1];

    return assignDocumentTypeToRange(documentType, startPg, endPg);
  };

  const assignDocumentTypeToRange = async (
    documentType: string,
    startPg: number,
    endPg: number,
  ): Promise<boolean> => {
    if (startPg > endPg) {
      KaboodleToaster.error("Start page must be before end page");
      return false;
    }

    const length = endPg - startPg + 1;

    // check for overlapping groups before proceeding
    const overlappingGroups = findOverlappingGroups(startPg, endPg);

    if (overlappingGroups.length > 0) {
      // block the operation if there's any overlap
      const overlappingRanges = overlappingGroups
        .map((group) => `${group.start_pg}-${group.end_pg}`)
        .join(", ");
      KaboodleToaster.error(
        `Selection overlaps with existing group(s) on pages ${overlappingRanges}. Delete or modify existing groups first.`,
      );
      return false;
    }

    try {
      // create the new document group
      await createDocumentGroup({
        metadoc: metadocId,
        start_pg: startPg,
        length,
        document_type: documentType,
        source: groupingSource,
      }).unwrap();

      const displayLabel = getDocumentTypeLabel(documentType);

      KaboodleToaster.success(
        `Document type "${displayLabel}" assigned to pages ${startPg}-${endPg}`,
      );
      setSelectedPages(new Set());

      // execute callback if provided (e.g. focusing thumbnails)
      if (onSuccess) {
        onSuccess();
      }
      return true;
    } catch (error) {
      console.error("Failed to create document group:", error);
      KaboodleToaster.error(
        "Failed to assign document type. Please try again.",
      );
      return false;
    }
  };

  const deleteEntry = async (entryId: string) => {
    // extract the document group ID from the entry ID (format: "group-{id}")
    const groupId = parseInt(entryId.replace(METADOC_GROUP_ID_PREFIX, ""), 10);

    if (isNaN(groupId)) {
      console.error("Invalid entry ID:", entryId);
      return;
    }

    let hasRegenerationJob = false;
    onRegenerationRequestStateChange?.(true);

    try {
      const response = (await deleteDocumentGroup(groupId).unwrap()) as {
        background_job_id?: number;
      } | void;

      if (response && typeof response.background_job_id === "number") {
        hasRegenerationJob = true;
        onRegenerationJobCreated?.(response.background_job_id);
      }

      KaboodleToaster.success("Document group deleted successfully");
    } catch (error) {
      console.error("Failed to delete document group:", error);
      KaboodleToaster.error(
        "Failed to delete document group. Please try again.",
      );
    } finally {
      if (!hasRegenerationJob) {
        onRegenerationRequestStateChange?.(false);
      }
    }
  };

  return {
    assignDocumentType,
    assignDocumentTypeToRange,
    deleteEntry,
  };
};
